import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Upload } from 'lucide-react';

import { SectionCard } from '../../../../components/design-system/admin/SectionCard';
import { getBase64 } from '../../../../common/utils/utils';

interface VendorLogoCardProps {
    vendor: any;
    setVendor: (vendor: any) => void;
    /** Show that the logo is required (a new vendor after a failed save). */
    showRequired?: boolean;
}

/** The vendor's logo, stored as base64 in `config.logo`. Click to pick or change it. */
export const VendorLogoCard = ({ vendor, setVendor, showRequired }: VendorLogoCardProps) => {
    const { t } = useTranslation();
    const logoInputRef = useRef<HTMLInputElement>(null);

    const pickImageHandler = () => {
        logoInputRef.current?.click();
    };

    const handleCompanyLogoChange = async (event) => {
        const file = event.target.files[0];
        const path = await getBase64(file);
        setVendor({
            ...vendor,
            config: {
                ...vendor.config,
                logo: path,
            },
        });
    };

    const logo = vendor.config?.logo;

    return (
        <SectionCard
            title={t('ADMIN_VENDOREDIT.LOGO.TITLE')}
            description={t('ADMIN_VENDOREDIT.LOGO.DESCRIPTION')}
            testId="vendor-logo-card"
        >
            <button
                type="button"
                onClick={pickImageHandler}
                aria-label={logo ? t('ADMIN_VENDOREDIT.LOGO.CHANGE') : t('ADMIN_VENDOREDIT.LOGO.UPLOAD')}
                data-invalid={showRequired || undefined}
                className="group relative flex h-56 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-input transition-colors hover:border-ring data-invalid:border-destructive"
            >
                {logo ? (
                    <>
                        <img
                            src={logo}
                            alt={t('ADMIN_VENDOREDIT.LOGO.ALT', { name: vendor.name || '' })}
                            className="max-h-full max-w-full object-contain p-4"
                        />
                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-foreground/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                            <span className="grid size-9 place-items-center rounded-lg bg-background text-foreground">
                                <Pencil className="size-4" />
                            </span>
                            <span className="text-sm font-medium text-background">{t('ADMIN_VENDOREDIT.LOGO.CHANGE')}</span>
                        </span>
                    </>
                ) : (
                    <span className="flex flex-col items-center gap-3">
                        <span className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground">
                            <Upload className="size-4" />
                        </span>
                        <span className="text-sm font-medium">{t('ADMIN_VENDOREDIT.LOGO.UPLOAD')}</span>
                        <span className="px-4 text-center text-xs text-muted-foreground">{t('ADMIN_VENDOREDIT.LOGO.HINT')}</span>
                    </span>
                )}
            </button>

            <input
                type="file"
                ref={logoInputRef}
                name="image"
                accept="image/*"
                onChange={handleCompanyLogoChange}
                className="hidden"
                data-testid="vendor-logo-input"
            />

            {showRequired && (
                <p className="mt-2 text-xs text-destructive" role="alert">
                    {t('ADMIN_VENDOREDIT.LOGO.REQUIRED')}
                </p>
            )}
        </SectionCard>
    );
};
